import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Plus } from "lucide-react";
import { LoadingState } from "components";

export interface AccountingFormLayoutProps {
  /** Page title */
  title: string;
  /** Subtitle/description */
  subtitle?: string;
  /** Current mode - edit or create */
  isEdit?: boolean;
  /** Whether form is saving */
  isSaving?: boolean;
  /** Whether form is loading */
  isLoading?: boolean;
  /** Loading message */
  loadingMessage?: string;
  /** Back button URL */
  backUrl: string;
  /** Back button label */
  backLabel?: string;
  /** Submit handler */
  onSubmit: (e?: React.FormEvent) => void;
  /** Submit button label */
  submitLabel?: string;
  /** Show add item button */
  showAddItemButton?: boolean;
  /** Add item handler */
  onAddItem?: () => void;
  /** Add item button label */
  addItemLabel?: string;
  /** Form children */
  children: React.ReactNode;
  /** Optional total amount to display */
  totalAmount?: number;
  /** Additional action buttons */
  extraActions?: React.ReactNode;
}

/**
 * Standard layout wrapper for accounting forms.
 * Includes:
 * - Header with title and action buttons
 * - Keyboard shortcuts (Ctrl+S to save, Ctrl+N to add item)
 * - Loading overlay
 * - Consistent styling
 */
const AccountingFormLayout: React.FC<AccountingFormLayoutProps> = ({
  title,
  subtitle,
  isEdit = false,
  isSaving = false,
  isLoading = false,
  loadingMessage,
  backUrl,
  backLabel = "Quay lại",
  onSubmit,
  submitLabel,
  showAddItemButton = false,
  onAddItem,
  addItemLabel = "Thêm dòng",
  children,
  totalAmount,
  extraActions,
}) => {
  const navigate = useNavigate();

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S or Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (!isSaving && !isLoading) {
          onSubmit();
        }
      }
      // Ctrl+N or Cmd+N to add item
      if ((e.ctrlKey || e.metaKey) && e.key === "n") {
        e.preventDefault();
        if (showAddItemButton && onAddItem && !isSaving && !isLoading) {
          onAddItem();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onSubmit, onAddItem, showAddItemButton, isSaving, isLoading]);

  const handleBack = useCallback(() => {
    navigate(backUrl);
  }, [navigate, backUrl]);

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      onSubmit(e);
    },
    [onSubmit],
  );

  const defaultSubmitLabel = isEdit ? "Cập nhật" : "Lưu";
  const defaultLoadingMessage = isEdit ? "Đang tải..." : "Đang lưu...";

  return (
    <>
      {(isLoading || isSaving) && (
        <LoadingState
          message={loadingMessage || defaultLoadingMessage}
          description="Vui lòng chờ trong giây lát."
        />
      )}

      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="min-w-0">
            <p className="text-sm text-gray-500 uppercase tracking-wide">
              {title}
            </p>
            <h1 className="text-2xl font-semibold text-gray-900 truncate">
              {isEdit
                ? `Cập nhật ${title.toLowerCase()}`
                : `Thêm ${title.toLowerCase()}`}
            </h1>
            {subtitle && (
              <p className="text-sm text-gray-600 mt-0.5">{subtitle}</p>
            )}
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Total amount display */}
            {totalAmount !== undefined && (
              <div className="text-right hidden sm:block">
                <p className="text-xs text-gray-500">Tổng tiền</p>
                <p className="text-lg font-semibold text-gray-900">
                  {totalAmount.toLocaleString("vi-VN")} đ
                </p>
              </div>
            )}

            {/* Add item button */}
            {showAddItemButton && onAddItem && (
              <button
                type="button"
                onClick={onAddItem}
                disabled={isSaving || isLoading}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Ctrl+N"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">{addItemLabel}</span>
              </button>
            )}

            {/* Extra actions */}
            {extraActions}

            {/* Back button */}
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{backLabel}</span>
            </button>

            {/* Submit button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving || isLoading}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed"
              title="Ctrl+S"
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Đang lưu..." : submitLabel || defaultSubmitLabel}
            </button>
          </div>
        </div>

        {/* Keyboard shortcuts hint */}
        <div className="hidden md:flex items-center gap-4 text-xs text-gray-400">
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded border border-gray-200 font-mono">
              Ctrl+S
            </kbd>{" "}
            Lưu
          </span>
          {showAddItemButton && (
            <span>
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded border border-gray-200 font-mono">
                Ctrl+N
              </kbd>{" "}
              Thêm dòng
            </span>
          )}
        </div>

        {/* Form content */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {children}
        </form>
      </div>
    </>
  );
};

export default AccountingFormLayout;
